/** Dashboard widget: sector relative-strength heatmap. */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { SectorStrength } from "@/types";
import WidgetCard from "./WidgetCard";

function heatColor(pct: number): string {
  if (pct >= 5) return "bg-signal-teal/30 text-signal-teal";
  if (pct >= 0) return "bg-signal-teal/10 text-mist-200";
  if (pct >= -5) return "bg-signal-rose/10 text-mist-200";
  return "bg-signal-rose/30 text-signal-rose";
}

export default function SectorHeatmapWidget(): React.ReactElement {
  const [sectors, setSectors] = useState<SectorStrength[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<SectorStrength[]>("/market/sector-strength/latest")
      .then(({ data }) => setSectors(Array.isArray(data) ? data : []))
      .catch(() => setError("Unable to load sector strength"));
  }, []);

  return (
    <WidgetCard title="Sector heatmap" isLoading={sectors === null && !error} error={error}>
      {sectors && sectors.length > 0 ? (
        <div className="grid grid-cols-2 gap-1.5">
          {sectors.map((s) => (
            <div
              key={s.id}
              className={`rounded-md px-2 py-1.5 text-xs ${heatColor(Number(s.relative_performance_pct))}`}
            >
              <p className="truncate font-medium">{s.sector}</p>
              <p className="font-mono">{Number(s.relative_performance_pct) > 0 ? "+" : ""}{s.relative_performance_pct}%</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-mist-400">No sector strength data recorded yet.</p>
      )}
    </WidgetCard>
  );
}
